import { useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import {
  BusinessOutlined as InsurersIcon,
  GridView as OverviewIcon,
  FeedOutlined as ContractsIcon,
  List as ClaimsIcon,
  Headset as ContactIcon,
  MapsHomeWorkOutlined as ObjectsIcon,
  MenuOpenOutlined as MenuOpenIcon,
  NoteAltOutlined as ControllingIcon,
  PeopleOutline as UsersIcon,
  EmailOutlined as EmailIcon,
  WbSunnyOutlined as SubstitutionsIcon,
  WorkOutlineOutlined as CustomersIcon,
  NotificationsActiveOutlined as ReminderIcon,
  Euro as AccountingIcon,
  AccountBalanceOutlined as BankAccountIcon,
  FileOpenSharp as BlogIcon,
  AccountBalanceOutlined as BankAccountsIcon,
  SettingsOutlined as SettingsIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router";
import PropTypes from "prop-types";
import { useAbility } from "../../hooks/permissions/useAbility.jsx";
import { useInsurerPermissions } from "../../hooks/permissions/useInsurerPermissions.jsx";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ContactModal from "@accredo/fe-shared/components/contact/ContactModal.jsx";
import PortalSwitcher from "@accredo/fe-shared/components/PortalSwitcher.jsx";
import { PORTAL } from "@accredo/fe-shared/theme/portals.js";
import { getEnv } from "@accredo/fe-shared/env.js";
import { colorsFoundation } from "@accredo/fe-shared/theme/tokens/colors-foundation.js";

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  width: "100%",
  height: "100%",
  transition: "all 0.3s ease",
}));

const LogoSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5),
  // alignItems: isMinimized ? 'center' : 'flex-start'
  alignItems: "flex-start",
}));

const LogoBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ theme, isMinimized }) => ({
  width: "auto",
  height: "56px",
  marginLeft: theme.spacing(0.75),
  borderRadius: isMinimized ? "4px" : "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  transition: "all 0.3s ease",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
}));

const TitleRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ isMinimized }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "space-between",
  width: "100%",
}));

const NavSection = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(0.75),
  width: "100%",
}));

const NavSectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  color: colorsFoundation.neutral[400],
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)} 0`,
}));

const NavItem = styled(RouterLink, {
  shouldForwardProp: (prop) =>
    prop !== "active" &&
    prop !== "isMinimized" &&
    prop !== "toolTip" &&
    prop !== "toolTipPlacement" &&
    prop !== "toolTipTitle",
})(({ theme, active, isMinimized }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "flex-start",
  gap: theme.spacing(1),
  height: "40px",
  padding: isMinimized
    ? theme.spacing(1)
    : `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  // borderRadius: theme.shape.borderRadius,
  borderRadius: 4,
  backgroundColor: active ? theme.palette.grey[100] : "transparent",
  color: theme.palette.primary.main,
  textDecoration: "none",
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: active ? theme.palette.grey[100] : theme.palette.grey[100],
  },
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    color: theme.palette.primary.main,
  },
}));

function SidebarNavItem({
  children,
  toolTip,
  toolTipPlacement,
  toolTipTitle,
  isVisible = true,
  ...props
}) {
  if (isVisible === false) {
    return null;
  }

  return (
    <NavItem {...props}>
      {toolTip ? (
        <Tooltip title={toolTipTitle} placement={toolTipPlacement}>
          {children[0]}
        </Tooltip>
      ) : (
        children[0]
      )}
      {children[1]}
    </NavItem>
  );
}

SidebarNavItem.propTypes = {
  children: PropTypes.node.isRequired,
  toolTip: PropTypes.bool,
  toolTipPlacement: PropTypes.string,
  toolTipTitle: PropTypes.string,
  isVisible: PropTypes.bool,
};

const NavText = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: 1.5,
  whiteSpace: "nowrap",
});

const Spacer = styled(Box)({
  flex: 1,
  minHeight: "1px",
});

const BottomSection = styled(Stack)({
  gap: 0,
});

const BottomLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ theme, isMinimized }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "flex-start",
  gap: theme.spacing(1),
  height: "40px",
  padding: isMinimized
    ? theme.spacing(1)
    : `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius,
  // borderRadius: 2,
  color: theme.palette.primary.main,
  textDecoration: "none",
  cursor: "pointer",
  transition: "all 0.2s",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    color: theme.palette.primary.main,
  },
}));

export default function Sidebar({ logoSrc, onToggle }) {
  const location = useLocation();
  const { can } = useAbility();
  const { canViewAny: canViewAnyInsurer } = useInsurerPermissions();
  const [isMinimized, setIsMinimized] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [contactOpen, setContactOpen] = useState(false);

  const isActive = (path) => {
    return path.length > 1
      ? location.pathname.startsWith(path)
      : location.pathname === path;
  };

  const handleToggle = () => {
    setIsMinimized(!isMinimized);
    if (onToggle) {
      onToggle(!isMinimized);
    }
  };

  const canViewCustomers = can("Customer", "viewAny");
  const canViewContracts = can("Contract", "viewAny");
  const canViewObjects = can("Object", "viewAny");
  const canViewInsurers = canViewAnyInsurer();
  const canViewBankNavAdmin = can("BankAccount", "viewNavAdmin");
  const canViewBankOwn = can("BankAccount", "viewOwn");
  const showStammdaten =
    canViewCustomers ||
    canViewContracts ||
    canViewObjects ||
    canViewInsurers ||
    canViewBankNavAdmin ||
    canViewBankOwn;

  const canViewBooking = can("booking", "viewAny");
  const canViewUsers = can("User", "viewAny");
  const canViewSubstitutions = can("Substitution", "viewAny");
  const canViewMail = can("Mail", "viewAny");
  const canViewAdminSettings = can("AdminSettings", "viewAny");
  const showVerwaltung =
    canViewBooking ||
    canViewUsers ||
    canViewSubstitutions ||
    canViewMail ||
    canViewAdminSettings;

  return (
    <SidebarContainer isMinimized={isMinimized}>
      <LogoSection isMinimized={isMinimized}>
        <LogoBox isMinimized={isMinimized}>
          {logoSrc && <img src={logoSrc} alt="Logo" />}
        </LogoBox>
        <TitleRow isMinimized={isMinimized}>
          {!isMinimized && (
            <PortalSwitcher
              current={PORTAL.SCHADENPORTAL}
              canSwitch={getEnv("VITE_HIDE_PORTAL_SWITCHER") !== "true"}
            />
          )}
          {isDesktop && (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{
                color: "text.subtle",
                padding: 1,
                transform: isMinimized ? "scaleX(-1)" : "none",
                transition: "transform 0.3s ease",
              }}
            >
              <MenuOpenIcon />
            </IconButton>
          )}
        </TitleRow>
      </LogoSection>

      <NavSection>
        <SidebarNavItem
          to="/"
          active={isActive("/")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Übersicht"
        >
          <OverviewIcon />
          {!isMinimized && <NavText>Übersicht</NavText>}
        </SidebarNavItem>
        <SidebarNavItem
          to="/schaeden"
          active={isActive("/schaeden")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Schäden"
          isVisible={can("Claim", "viewAny")}
        >
          <ClaimsIcon />
          {!isMinimized && <NavText>Schäden</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/controlling"
          active={isActive("/controlling")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Controlling"
          isVisible={can("Controlling", "viewAny")}
        >
          <ControllingIcon />
          {!isMinimized && <NavText>Controlling</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/reminders"
          active={isActive("/reminders")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Meine Erinnerungen & Termine"
          isVisible={true}
        >
          <ReminderIcon />
          {!isMinimized && <NavText>Erinnerungen</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/blog"
          active={isActive("/blog")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Blog"
          isVisible={can("Blog", "viewAny")}
        >
          <BlogIcon />
          {!isMinimized && <NavText>Was ist neu?</NavText>}
        </SidebarNavItem>
      </NavSection>

      {showStammdaten && (
        <NavSection>
          {!isMinimized && <NavSectionLabel>Stammdaten</NavSectionLabel>}

          <SidebarNavItem
            to="/admin/kunden"
            active={isActive("/admin/kunden")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Kunden"
            isVisible={canViewCustomers}
          >
            <CustomersIcon />
            {!isMinimized && <NavText>Kunden</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/vertraege"
            active={isActive("/admin/vertraege")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Verträge"
            isVisible={canViewContracts}
          >
            <ContractsIcon />
            {!isMinimized && <NavText>Verträge</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/objekte"
            active={isActive("/admin/objekte")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Objekte"
            isVisible={canViewObjects}
          >
            <ObjectsIcon />
            {!isMinimized && <NavText>Objekte</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/insurers"
            active={isActive("/admin/insurers")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Versicherungsgesellschaften"
            isVisible={canViewInsurers}
          >
            <InsurersIcon />
            {!isMinimized && <NavText>Versicherer</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/bankaccounts"
            active={isActive("/admin/bankaccounts")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Bankverbindungen"
            isVisible={canViewBankNavAdmin}
          >
            <BankAccountIcon />
            {!isMinimized && <NavText>Bankverbindungen</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/bankkonten"
            active={isActive("/bankkonten")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Bankverbindungen"
            isVisible={canViewBankOwn}
          >
            <BankAccountsIcon />
            {!isMinimized && <NavText>Bankverbindungen</NavText>}
          </SidebarNavItem>
        </NavSection>
      )}

      {showVerwaltung && (
        <NavSection>
          {!isMinimized && <NavSectionLabel>Verwaltung</NavSectionLabel>}

          <SidebarNavItem
            to="/admin/buchungen"
            active={isActive("/admin/buchungen")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Buchungsverwaltung"
            isVisible={canViewBooking}
          >
            <AccountingIcon />
            {!isMinimized && <NavText>Buchungen</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/users"
            active={isActive("/admin/users")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Benutzer"
            isVisible={canViewUsers}
          >
            <UsersIcon />
            {!isMinimized && <NavText>Benutzer</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/substitutions"
            active={isActive("/substitutions")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Stellvertretungen"
            isVisible={canViewSubstitutions}
          >
            <SubstitutionsIcon />
            {!isMinimized && <NavText>Stellvertretungen</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/emails"
            active={isActive("/admin/emails")}
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Eingehende und ausgehende E-Mails"
            isVisible={canViewMail}
          >
            <EmailIcon />
            {!isMinimized && <NavText>E-Mails</NavText>}
          </SidebarNavItem>

          <SidebarNavItem
            to="/admin/settings"
            active={
              isActive("/admin/settings") ||
              isActive("/admin/imports") ||
              isActive("/admin/sparten") ||
              isActive("/admin/schaden-ursachen") ||
              isActive("/admin/claim-forms") ||
              isActive("/admin/consent-documents") ||
              isActive("/admin/blog") ||
              isActive("/admin/workflow")
            }
            isMinimized={isMinimized}
            toolTip={isMinimized}
            toolTipPlacement="right"
            toolTipTitle="Einstellungen"
            isVisible={canViewAdminSettings}
          >
            <SettingsIcon />
            {!isMinimized && <NavText>Einstellungen</NavText>}
          </SidebarNavItem>
        </NavSection>
      )}

      <Spacer />

      <BottomSection>
        {/* disable for now until we have help page
        <BottomLink to="/hilfe" isMinimized={isMinimized}>
          <HelpIcon />
          {!isMinimized && <NavText>Hilfe</NavText>}
        </BottomLink>
        */}
        <BottomLink
          to="#"
          isMinimized={isMinimized}
          onClick={(e) => {
            e.preventDefault();
            setContactOpen(true);
          }}
        >
          <ContactIcon />
          {!isMinimized && <NavText>Kontakt</NavText>}
        </BottomLink>
      </BottomSection>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </SidebarContainer>
  );
}

Sidebar.propTypes = {
  logoSrc: PropTypes.string,
  onToggle: PropTypes.func,
};
